<!-- 通知消息列表：展示分页消息列表 -->
<template>
  <div class="panel-list">
    <header class="list-header">
      <h3 class="list-title">{{ $t('notifications') }}</h3>
      <button
        type="button"
        class="refresh-btn"
        :aria-label="$t('refresh')"
        :disabled="loading"
        @click="emit('refresh')"
      >
        <el-icon :size="18" :class="{ 'is-spinning': loading }">
          <Refresh />
        </el-icon>
      </button>
    </header>

    <div v-loading="loading" class="list-body">
      <div v-if="error" class="panel-empty">{{ $t('loadFailed') }}</div>
      <div v-else-if="!loading && list.length === 0" class="panel-empty">
        {{ $t('noNotifications') }}
      </div>
      <div v-else class="message-list-wrap">
        <ul class="message-list">
          <li
            v-for="item in list"
            :key="item.id"
            class="message-item"
            :class="{ 'is-unread': isUnread(item) }"
            @click="emit('open-detail', item.id)"
          >
            <div class="item-title-row">
              <span
                class="item-dot"
                :class="{ 'is-visible': isUnread(item) }"
              />
              <span class="item-title">{{ item.title }}</span>
            </div>
            <time class="item-time">{{
              formatNotificationTime(item.createTime)
            }}</time>
          </li>
        </ul>
      </div>
    </div>

    <footer v-if="total > pageSize" class="panel-footer">
      <el-pagination
        small
        layout="prev, pager, next"
        :current-page="pageNum"
        :page-size="pageSize"
        :total="total"
        @current-change="emit('page-change', $event)"
      />
    </footer>
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { Refresh } from '@element-plus/icons-vue'
import { isNotificationUnread, type NotificationItem } from '@/api/notification'
import { formatNotificationTime } from './useNotification'

defineProps<{
  list: NotificationItem[]
  loading: boolean
  error: boolean
  pageNum: number
  pageSize: number
  total: number
}>()

const emit = defineEmits<{
  'page-change': [page: number]
  'open-detail': [id: number]
  refresh: []
}>()

const $t: any = inject('$t')
const isUnread = isNotificationUnread
</script>

<style lang="scss" scoped>
.panel-list {
  display: flex;
  flex-direction: column;
  max-width: 320px;
  min-height: 160px;
  max-height: min(480px, calc(100vh - 120px));
}

.list-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 36px 12px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 8px;
}

.refresh-btn {
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--el-color-primary);
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--el-color-primary-light-9);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .is-spinning {
    animation: notification-refresh-spin 0.8s linear infinite;
  }
}

@keyframes notification-refresh-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.list-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  color: #303133;
}

.list-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100px;
  overflow: hidden;
}

.message-list-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 4px;
  }
}

.message-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.message-item {
  padding: 10px 4px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background-color 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fb;
  }

  &.is-unread .item-title {
    font-weight: 600;
    color: #303133;
  }
}

.item-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.item-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.is-visible {
    background-color: #ff4d4f;
  }
}

.item-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-time {
  display: block;
  margin-top: 4px;
  margin-left: 12px;
  font-size: 11px;
  line-height: 1.3;
  color: #a8abb2;
}

.panel-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #909399;
  font-size: 14px;
}

.panel-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding-top: 10px;
  margin-top: 4px;
  border-top: 1px solid #ebeef5;
}
</style>
